import { FC, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TransactionStatus,
  TransactionTypesOptions,
  withStyles,
  Card,
  Box,
  formatAmount,
  removeWhiteSpaceURI,
} from 'vopay';
import { Title, UtilButton, StatusIcon, CircleButton } from 'UI';
import { format } from 'date-fns';
import { useClientData } from '@/providers/ClientData.provider';
import { useToast } from '@/providers/Toast.provider';
import useTransactionHistory from '@/hooks/useApi/useTransactionHistory';
import styles from './TransactionCard.module.scss';

type TransactionCardProps = {
  receiverName: string;
  amount: string;
  currency: string;
  transactionId: string;
  parentTransactionId?: string;
  type: TransactionTypesOptions;
  parentType?: TransactionTypesOptions;
  failureReason: string;
  senderName?: string;
  contactId?: string;
};

const FAILED_STATUSES = [TransactionStatus.FAILED, TransactionStatus.CANCELLED, TransactionStatus.DECLINED];
const FINAL_STATUSES = [
  TransactionStatus.SUCCESSFUL,
  TransactionStatus.COMPLETE,
  TransactionStatus.FAILED,
  TransactionStatus.CANCELLED,
  TransactionStatus.SETTLED,
  TransactionStatus.DECLINED,
];

const cn = withStyles(styles);

const TransactionCard: FC<TransactionCardProps> = ({
  receiverName,
  amount,
  currency,
  transactionId,
  parentTransactionId,
  type,
  parentType = 'All',
  failureReason,
  senderName,
  contactId,
}) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Card',
  });
  const { t: tStatus } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.statuses',
  });
  const {
    response: { clientAccountsEnabled },
  } = useClientData();
  const toast = useToast();
  const { transactionHistory, fetchTransactionHistory } = useTransactionHistory();

  const isWithdrawal = type.toLowerCase().includes('withdrawal');
  const isFunding = type.toLowerCase().includes('funding');

  useEffect(() => {
    if (!transactionId) return;

    (async () => {
      try {
        await fetchTransactionHistory({
          TransactionID: transactionId,
        });
      } catch (err) {
        console.error(err);
        toast.neutral(t('fetchingError', { defaultValue: 'Failed to fetch transaction history' }));
      }
    })();
  }, [transactionId]);

  const containerRef = useRef<HTMLDivElement>(null);
  const stepsRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  const STEP = 50;
  const [offset, setOffset] = useState(0);
  const [maxOffset, setMaxOffset] = useState(0);

  const handleDown = () => {
    setOffset((prev) => Math.min(prev + STEP, maxOffset));
  };

  const handleUp = () => {
    setOffset((prev) => Math.max(prev - STEP, 0));
  };

  useEffect(() => {
    if (!containerRef.current || !stepsRef.current) return;

    const containerHeight = containerRef.current.offsetHeight;
    const stepsHeight = stepsRef.current.offsetHeight;
    const overflow = stepsHeight > containerHeight;

    const calculatedMaxOffset = Math.max(0, stepsHeight - containerHeight);

    setHasOverflow(overflow);
    setMaxOffset(calculatedMaxOffset);

    setOffset(calculatedMaxOffset);
  }, [transactionHistory]);

  const statusMessages: Record<TransactionStatus, string> = {
    [TransactionStatus.REQUESTED]: t('StatusMessage.requested', {
      defaultValue: 'This transaction request has been requested.',
    }),
    [TransactionStatus.PENDING]: t('StatusMessage.pending', { defaultValue: 'This transaction is pending.' }),
    [TransactionStatus.IN_PROGRESS]: t('StatusMessage.inProgress', {
      defaultValue: 'This transaction is in progress.',
    }),
    [TransactionStatus.SENT]: t('StatusMessage.sent', { defaultValue: 'This transaction has been sent.' }),
    [TransactionStatus.SUCCESSFUL]: t('StatusMessage.complete', {
      defaultValue: 'This transaction completed successfully.',
    }),
    [TransactionStatus.COMPLETE]: t('StatusMessage.complete', {
      defaultValue: 'This transaction completed successfully.',
    }),
    [TransactionStatus.SETTLED]: t('StatusMessage.settled', { defaultValue: 'This transaction has been settled.' }),
    [TransactionStatus.FAILED]: t('StatusMessage.failed', { defaultValue: 'This transaction has been failed.' }),
    [TransactionStatus.CANCELLED]: t('StatusMessage.cancelled', {
      defaultValue: 'This transaction has been cancelled.',
    }),
    [TransactionStatus.PENDING_APPROVAL]: t('StatusMessage.pendingApproval', {
      defaultValue: 'This transaction is awaiting approval.',
    }),
    [TransactionStatus.WAITING]: t('StatusMessage.waiting', { defaultValue: 'This transaction is waiting.' }),
    [TransactionStatus.CANCELLATION_REQUESTED]: t('StatusMessage.cancellationRequested', {
      defaultValue: 'Cancellation has been requested.',
    }),
    [TransactionStatus.BLOCKED_BY_FINANCIAL_INSTITUTION]: t('StatusMessage.blocked', {
      defaultValue: 'This transaction was blocked by financial institution.',
    }),
    [TransactionStatus.PENDING_CONFIRMATION]: t('StatusMessage.pendingConfirmation', {
      defaultValue: 'This transaction is waiting for confirmation.',
    }),
    [TransactionStatus.DECLINED]: t('StatusMessage.declined', { defaultValue: 'This transaction has been declined.' }),
    [TransactionStatus.RECEIVED]: t('StatusMessage.received', { defaultValue: 'This transaction has been received.' }),
    [TransactionStatus.BLANK]: t('StatusMessage.unknown', { defaultValue: 'Unknown status.' }),
  };

  return (
    <Card className={cn('card')}>
      <Box jc="sb" gapSize="24px">
        <Box flx="col" gapSize="24px">
          <Title level="2">
            {type === 'Account Transfer' && senderName ? (
              <>
                {senderName} <span className={cn('grey')}>{t('to', { defaultValue: 'to' })}</span> {receiverName}
              </>
            ) : contactId ? (
              <UtilButton
                className={cn('name')}
                onClick={() => {
                  if (clientAccountsEnabled) {
                    window.open(`/client-accounts/${contactId}`, '_blank');
                  } else {
                    window.open(`/contacts/${contactId}`, '_blank');
                  }
                }}
              >
                {receiverName}
              </UtilButton>
            ) : (
              receiverName
            )}
          </Title>
          <Box flx="col" gapSize="32px" padding="0 0 0 6px">
            <Box flx="col" gapSize="5px">
              <Title level="5" emphasized={false} className={cn('grey')}>
                {isWithdrawal
                  ? t('amountSent', { defaultValue: 'Amount Sent' })
                  : isFunding
                    ? t('amountRequested', { defaultValue: 'Amount Requested' })
                    : t('amount', { defaultValue: 'Amount' })}
              </Title>
              <Title level="4">
                {currency} {formatAmount(amount)}
              </Title>
            </Box>

            <div className={cn('idContainer')}>
              <Box flx="col" gapSize="6px">
                <div className={cn('grey', 'row')}>{t('transactionId', { defaultValue: 'Transaction ID' })}</div>
                <div className={cn('fontWeight500')}>{transactionId}</div>
              </Box>
              {parentTransactionId && parentType && (
                <div className={cn('second')}>
                  <div className={cn('grey', 'row')}>
                    {t('parentTransactionId', { defaultValue: 'Parent Transaction ID' })}
                  </div>
                  <UtilButton
                    onClick={() => {
                      window.open(
                        `/transactions-history/details/${removeWhiteSpaceURI(parentType)}/${parentTransactionId}/${currency}`,
                        '_blank',
                      );
                    }}
                  >
                    {parentTransactionId}
                  </UtilButton>
                </div>
              )}
            </div>
          </Box>
        </Box>
        {transactionHistory.length > 0 && (
          <div className={cn('statusWrapper')}>
            <div className={cn('statusContainer')} ref={containerRef}>
              <div
                className={cn(hasOverflow && 'statusSteps')}
                ref={stepsRef}
                style={hasOverflow ? { top: -offset } : {}}
              >
                {transactionHistory.map((item, index) => {
                  return (
                    <div
                      className={cn('status', !isFinalStep(index) && 'border', isFailed(item.Status) ? 'red' : 'green')}
                      key={`${item.Status}-${item.DateAdded}`}
                    >
                      <div className={cn('icon', !isFinalStep(index) && 'small')}>
                        <StatusIcon
                          status={getStatusIcon(item.Status, index)}
                          size={isFinalStep(index) ? 'medium' : 'small'}
                        />
                      </div>

                      <Box flx="col" gapSize="4px">
                        <Title level={isFinalStep(index) ? '4' : '5'} className={cn('capitalize')}>
                          {tStatus(`Transaction.TransactionHistory.statuses.${item.Status}`, {
                            defaultValue: item.Status,
                          })}
                        </Title>
                        {transactionHistory.length === index + 1 && (
                          <Title level="6" emphasized={false} className={cn('subTitle', 'grey')}>
                            {item.Status === 'failed' && failureReason ? failureReason : statusMessages[item.Status]}
                          </Title>
                        )}

                        <div className={cn('text')}>{format(item.DateAdded, 'dd MMM yyyy, HH:mm:ss')}</div>
                      </Box>
                    </div>
                  );
                })}
              </div>
            </div>
            {hasOverflow && offset > 0 && (
              <div className={cn('fadeOverlay', 'top')}>
                <CircleButton
                  icon="keyboard_arrow_up"
                  size="large"
                  circleFill
                  className={cn('arrowButton', 'up')}
                  onClick={handleUp}
                />
              </div>
            )}

            {hasOverflow && offset < maxOffset && (
              <div className={cn('fadeOverlay', 'bottom')}>
                <CircleButton
                  icon="keyboard_arrow_down"
                  size="large"
                  circleFill
                  className={cn('arrowButton', 'down')}
                  onClick={handleDown}
                />
              </div>
            )}
          </div>
        )}
      </Box>
    </Card>
  );

  function isFinalStep(index: number) {
    return transactionHistory.length === index + 1 || false;
  }

  function isFailed(status: TransactionStatus) {
    return FAILED_STATUSES.includes(status);
  }

  function getStatusIcon(status: TransactionStatus, index: number): 'pending' | 'successful' | 'failed' {
    if (isFailed(status)) return 'failed';
    if (isFinalStep(index) && !FINAL_STATUSES.includes(status)) return 'pending';

    return 'successful';
  }
};

export default TransactionCard;
